/**
 * ListColumnsPanel — zakładka „Kolumny" w Kreatorze Listy (osobny widok).
 *
 * Przeniesienie konfiguracji kolumn z modala (`ColumnConfigModal`) do widoku
 * Kreatora Listy. Czyta aktywny layout listy przez `useTableLayout(slug)`,
 * pozwala: przełączyć widoczność, zmienić kolejność (**drag-and-drop**, jak w
 * modalu), ustawić szerokość i własną etykietę — zapis przez `saveLayout`
 * (PUT list_config, zachowuje sort/filtry/formattingRules).
 *
 * Etykiety kolumn z lekkiego rejestru (`list-column-registry`) — generyczny
 * widok nie ma dostępu do definicji kolumn ze stron. Nieznane id → surowe id.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnConfig } from '@saas/shared';
import { Eye, EyeOff, GripVertical, Plus, Save, X } from 'lucide-react';

import { useReferenceListContext } from '@/contexts/ReferenceListContext';
import { useToast } from '@/contexts/ToastContext';

import { useColumnLabel } from './list-column-registry';
import { resolveListColumnSet } from './list-column-set';
import { useTableLayout } from './useTableLayout';

export function ListColumnsPanel({ slug }: { slug: string }): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);
  // Hub Krok 2: provider etykiet świadomy trybu `template` (pola szablonu po nazwie).
  const columnLabel = useColumnLabel(slug);
  // Paleta „Dodaj kolumnę" — dostępna tylko w kontekście menu-embed (ReferenceListContext).
  // undefined gdy brak kontekstu (standalone builder) → sekcja nie renderuje się.
  const referenceCtx = useReferenceListContext();
  const availableFields = referenceCtx?.availableFields;
  const onDirtyChange = referenceCtx?.onDirtyChange;

  // Kolumny, które lista umie pokazać (katalog pól ∪ built-iny ważne w tym kontekście ∪ kolumny
  // strony spoza katalogu z rejestru, np. `company`) — JEDNO źródło prawdy wspólne z zakładką
  // „Kalendarz" (`resolveListColumnSet`). undefined gdy `availableFields` nieznane
  // (standalone/nieznany kontekst) — brak podstawy do filtrowania i do palety.
  const columnSet = resolveListColumnSet({ slug, referenceCtx, columnLabel, t });
  // Zbiór DOZWOLONY dla soft-healu zapisu.
  const allowedColumnIds = columnSet?.allowedIds;

  // Lokalna kopia kolumn w kolejności wyświetlania (index = order).
  const [cols, setCols] = useState<ColumnConfig[] | null>(null);
  // Wyszukiwarka w palecie „Dodaj kolumnę" (filtruje po nazwie pola).
  const [search, setSearch] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    const layoutCols = layout?.layoutConfig?.columns;
    if (layoutCols) {
      setCols([...layoutCols].sort((a, b) => a.order - b.order));
    }
  }, [layout]);

  if (isLoading) {
    return <p className="text-sm text-text-tertiary">…</p>;
  }
  if (!layout || !cols) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  const label = (col: ColumnConfig): string => {
    if (col.customLabel) return col.customLabel;
    return columnLabel(col.id) ?? col.id;
  };

  const markDirty = () => onDirtyChange?.(true);

  const toggleVisible = (id: string) => {
    markDirty();
    setCols((cs) => cs!.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c)));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      markDirty();
      setCols((cs) => {
        if (!cs) return cs;
        const oldIndex = cs.findIndex((c) => c.id === active.id);
        const newIndex = cs.findIndex((c) => c.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return cs;
        return arrayMove(cs, oldIndex, newIndex);
      });
    }
  };

  const setWidth = (id: string, raw: string) => {
    markDirty();
    setCols((cs) =>
      cs!.map((c) => {
        if (c.id !== id) return c;
        const n = parseInt(raw, 10);
        return { ...c, width: Number.isNaN(n) ? c.width : n };
      }),
    );
  };

  const setLabel = (id: string, value: string) => {
    markDirty();
    setCols((cs) =>
      cs!.map((c) => (c.id === id ? { ...c, customLabel: value.trim() || undefined } : c)),
    );
  };

  // Dodaje nową kolumnę do listy (koniec kolejności, visible=true).
  // Pomija pola, które już są na liście (guard przed duplikatami).
  const addColumn = (fieldId: string) => {
    markDirty();
    setCols((cs) => {
      if (!cs) return cs;
      if (cs.some((c) => c.id === fieldId)) return cs;
      return [...cs, { id: fieldId, visible: true, width: 'auto' as const, order: cs.length }];
    });
  };

  // Usuwa kolumnę z listy — po usunięciu wraca do palety „Dodaj kolumnę".
  const removeColumn = (fieldId: string) => {
    markDirty();
    setCols((cs) => cs?.filter((c) => c.id !== fieldId) ?? cs);
  };

  const handleSave = async () => {
    // Soft-heal: przy każdym zapisie odsiewamy kolumny-sieroty — id spoza zbioru
    // dozwolonego (np. `record_name` zaszyty w starym configu kartoteki systemowej,
    // albo pole dawno usunięte z katalogu). Bez tego martwe id zostaje w zapisanym
    // list_config na zawsze (render listy je i tak dropuje, ale Kreator wystawia je
    // przy każdym kolejnym otwarciu). Gdy allowedColumnIds===undefined (kontekst
    // nieznany/standalone) — nie odsiewamy, brak podstawy do decyzji.
    const healedCols = allowedColumnIds ? cols.filter((c) => allowedColumnIds.has(c.id)) : cols;
    const reordered = healedCols.map((c, i) => ({ ...c, order: i }));
    await saveLayout({ ...layout.layoutConfig, columns: reordered });
    onDirtyChange?.(false);
    toast.show({ variant: 'success', title: t('tenant.listBuilder.columns.saved') });
  };

  return (
    <div className="space-y-4" data-testid="list-columns-panel">
      <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.columns.intro')}</p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={cols.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {cols.map((col) => (
              <SortableColumnRow
                key={col.id}
                col={col}
                label={label(col)}
                dragAria={t('tenant.listBuilder.columns.dragAria')}
                visibleAria={t('tenant.listBuilder.columns.visibleAria')}
                removeAria={t('tenant.listBuilder.columns.removeColumnAria')}
                labelPlaceholder={t('tenant.listBuilder.columns.labelPlaceholder')}
                widthLabel={t('tenant.listBuilder.columns.widthLabel')}
                onToggleVisible={() => toggleVisible(col.id)}
                onSetWidth={(raw) => setWidth(col.id, raw)}
                onSetLabel={(value) => setLabel(col.id, value)}
                onRemove={availableFields !== undefined ? () => removeColumn(col.id) : undefined}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Paleta „Dodaj kolumnę" — widoczna tylko w kontekście menu-embed */}
      {columnSet &&
        cols &&
        (() => {
          const presentIds = new Set(cols.map((c) => c.id));
          // Kandydaci = katalog ∪ built-iny ∪ kolumny spoza katalogu z rejestru (Firma, Kategoria,
          // szczegóły umowy, Nadawca…), deduplikowani po id — patrz `resolveListColumnSet`. Bez
          // kolumn rejestru kolumny usuniętej z listy (np. Kategorii) nie dałoby się przywrócić.
          const toAdd = columnSet.candidates.filter((f) => !presentIds.has(f.id));
          if (toAdd.length === 0) return null;
          const filtered = search.trim()
            ? toAdd.filter((f) => f.name.toLowerCase().includes(search.trim().toLowerCase()))
            : toAdd;
          return (
            <div className="space-y-2" data-testid="list-columns-add-section">
              <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
                {t('tenant.listBuilder.columns.addColumn')}
              </p>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin.templateMenuBuilder.viewBuilder.searchFields')}
                className="w-full rounded border border-border-secondary bg-bg-elevated px-2 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-accent-500"
                data-testid="list-columns-search"
              />
              <div className="flex flex-wrap gap-2">
                {filtered.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => addColumn(f.id)}
                    className="inline-flex items-center gap-1 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-xs text-text-primary hover:bg-bg-secondary"
                    data-testid={`list-columns-add-${f.id}`}
                  >
                    <Plus size={12} aria-hidden="true" />
                    {f.name}
                  </button>
                ))}
                {filtered.length === 0 && search.trim() && (
                  <p className="text-xs text-text-tertiary">
                    {t('tenant.listBuilder.columns.noFieldsMatch')}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid="list-columns-save"
        >
          <Save size={16} aria-hidden="true" />
          {isSaving
            ? t('tenant.listBuilder.columns.saving')
            : t('tenant.listBuilder.columns.saveBtn')}
        </button>
      </div>
    </div>
  );
}

function SortableColumnRow({
  col,
  label,
  dragAria,
  visibleAria,
  removeAria,
  labelPlaceholder,
  widthLabel,
  onToggleVisible,
  onSetWidth,
  onSetLabel,
  onRemove,
}: {
  col: ColumnConfig;
  label: string;
  dragAria: string;
  visibleAria: string;
  removeAria: string;
  labelPlaceholder: string;
  widthLabel: string;
  onToggleVisible: () => void;
  onSetWidth: (raw: string) => void;
  onSetLabel: (value: string) => void;
  /** Gdy zdefiniowane — pokazuje przycisk X (tylko w kontekście menu-embed). */
  onRemove?: () => void;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-lg border border-border-secondary bg-bg-elevated px-3 py-2"
      data-testid={`list-columns-row-${col.id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={dragAria}
        title={dragAria}
        className="cursor-grab touch-none text-text-muted hover:text-text-primary active:cursor-grabbing"
        data-testid={`list-columns-drag-${col.id}`}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>

      <button
        type="button"
        onClick={onToggleVisible}
        aria-label={visibleAria}
        title={visibleAria}
        className={col.visible ? 'text-accent-600' : 'text-text-muted'}
        data-testid={`list-columns-visible-${col.id}`}
      >
        {col.visible ? (
          <Eye size={16} aria-hidden="true" />
        ) : (
          <EyeOff size={16} aria-hidden="true" />
        )}
      </button>

      <span className="min-w-[160px] text-sm font-medium text-text-primary">{label}</span>

      <input
        type="text"
        defaultValue={col.customLabel ?? ''}
        onBlur={(e) => onSetLabel(e.target.value)}
        placeholder={labelPlaceholder}
        className="flex-1 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
      />

      <label className="flex items-center gap-1 text-xs text-text-tertiary">
        {widthLabel}
        <input
          type="number"
          min={40}
          value={typeof col.width === 'number' ? col.width : ''}
          onChange={(e) => onSetWidth(e.target.value)}
          className="w-20 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
        />
      </label>

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeAria}
          title={removeAria}
          className="ml-auto text-text-muted hover:text-error-500"
          data-testid={`list-columns-remove-${col.id}`}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
